import React, { useEffect, useState } from "react";

type GlowBarsProps = {
  bars?: number; // default 16
  intervalMs?: number; // default 350
  widthPx?: number; // bar width, default 16
  gapPx?: number; // gap between bars, default 12
  className?: string; // extra classes for container
  mode?: "animated" | "percent"; // how to light bars
  percent?: number; // 0..100 when mode === "percent"
};

const glowStyle: React.CSSProperties = {
  borderRadius: 2,
  border: "2px solid rgba(0, 193, 159, 0.28)",
  background:
    "linear-gradient(to bottom right, #00C19F 0%, #099D83 50%) bottom right / 50% 50% no-repeat, " +
    "linear-gradient(to bottom left, #00C19F 0%, #099D83 50%) bottom left / 50% 50% no-repeat, " +
    "linear-gradient(to top left, #00C19F 0%, #099D83 50%) top left / 50% 50% no-repeat, " +
    "linear-gradient(to top right, #00C19F 0%, #099D83 50%) top right / 50% 50% no-repeat",
  boxShadow: "0 0 8px 0 rgba(0, 193, 159, 0.21)"
};

const dimStyle: React.CSSProperties = {
  borderRadius: 2,
  border: "2px solid rgba(0, 193, 159, 0.14)",
  background:
    "linear-gradient(to bottom right, rgba(0, 193, 159, 0.50) 0%, rgba(9, 157, 131, 0.50) 50%) bottom right / 50% 50% no-repeat, " +
    "linear-gradient(to bottom left, rgba(0, 193, 159, 0.50) 0%, rgba(9, 157, 131, 0.50) 50%) bottom left / 50% 50% no-repeat, " +
    "linear-gradient(to top left, rgba(0, 193, 159, 0.50) 0%, rgba(9, 157, 131, 0.50) 50%) top left / 50% 50% no-repeat, " +
    "linear-gradient(to top right, rgba(0, 193, 159, 0.50) 0%, rgba(9, 157, 131, 0.50) 50%) top right / 50% 50% no-repeat"
};

const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max);

export const IncLoadingBars: React.FC<GlowBarsProps> = ({
  bars = 16,
  intervalMs = 350,
  widthPx = 16,
  gapPx = 12,
  className = "",
  mode = "percent",
  percent = 0
}) => {
  const [activeCount, setActiveCount] = useState(0); // used in animated mode

  // Animate only when mode === "animated"
  useEffect(() => {
    if (mode !== "animated") {
      return;
    }
    const id = setInterval(() => {
      setActiveCount(c => (c >= bars ? 0 : c + 1));
    }, intervalMs);
    return () => clearInterval(id);
  }, [bars, intervalMs, mode]);

  // Determine how many bars should be lit
  const activeTarget = mode === "percent" ? Math.round((clamp(percent, 0, 100) / 100) * bars) : activeCount;

  return (
    <div
      aria-label="progress glow bars"
      className={`inc-flex-row ${className}`}
      style={{
        gap: gapPx,
        height: "100%"
      }}
    >
      {Array.from({ length: bars }).map((_, i) => {
        const lit = i < activeTarget;
        return (
          <div
            key={i}
            style={{
              width: widthPx,
              maxHeight: "40px",
              ...(lit ? glowStyle : dimStyle),
              transition: "filter 200ms ease, box-shadow 200ms ease, border-color 200ms ease"
            }}
          />
        );
      })}
    </div>
  );
};
